import { useEffect, useRef, useState } from 'react';
import { usePathname, useRouter } from 'expo-router';
import { useAuthStore, useChatInputStore } from '@/store';
import { getChatInputContextIdFromPathname } from '@/lib/chatInputContext';
import { openNewChatComposer } from '@/lib/openHomeComposer';
import { BROWSER_EXTENSION_ORIGIN, formatBrowserContext, getBrowserPanelSession, parseBrowserPanelSession, setBrowserPanelSession } from '@/lib/browserPanel';
import { browserPanelGuest, signInBrowserPanel } from '@/lib/browserPanelSignIn';
import { API_HOST, fetchWithTimeout, fetchWithAuth } from '@/lib/api/client';
import { createXReplyGenerator } from '@/lib/browserPanelXReply';

export default function BrowserPanelBridge() {
  const router = useRouter();
  const pathname = usePathname();
  const userId = useAuthStore(state => state.user?.id || null);
  const guest = useAuthStore(browserPanelGuest);
  const [macAvailable, setMacAvailable] = useState(false);
  const attemptedSignIn = useRef(false);
  const priorUser = useRef(userId);
  const [pending, setPending] = useState(null);
  const [connected, setConnected] = useState(false);
  const added = useRef(new Set());
  const [panel, setPanel] = useState(getBrowserPanelSession);
  const currentPath = useRef(pathname); currentPath.current = pathname;

  useEffect(() => {
    if (!panel && window.parent === window) return;
    const send = (type, extra = {}) => window.parent.postMessage({ channel: 'gab-browser-panel', nonce: panel.nonce, type, ...extra }, BROWSER_EXTENSION_ORIGIN);
    const xReplies = createXReplyGenerator({ getState: useAuthStore.getState, fetchWithAuth, send });
    const onMessage = event => {
      const data = event.data;
      if (event.source !== window.parent || event.origin !== BROWSER_EXTENSION_ORIGIN || data?.channel !== 'gab-browser-panel') return;
      // Routers and auth redirects can remove the initial URL fragment before
      // React mounts. Only the authenticated parent may re-establish it.
      if (data.type === 'hello' && !panel) {
        const next = parseBrowserPanelSession({ framed: true, search: '?browserPanel=1', hash: `#gab-panel=${data.nonce}` });
        if (next) { setBrowserPanelSession(next); setPanel(next); }
        return;
      }
      if (!panel || data.nonce !== panel.nonce) return;
      if (data.type === 'mac-account-available') setMacAvailable(true);
      if (data.type === 'hello') { setConnected(true); send('ready', { path: currentPath.current, capabilities: ['x-reply-v1'] }); }
      if (data.type === 'x-reply') void xReplies.generate(data.request);
      if (data.type === 'x-reply-cancel') xReplies.cancel(data.id);
      if (data.type === 'new-chat') { setPending(null); openNewChatComposer(router, { replace: true }); }
      if (data.type === 'context') {
        const text = formatBrowserContext(data.context);
        if (!text) { send('context-error'); return; }
        if (added.current.has(data.context.id)) { send('context-added', { id: data.context.id }); return; }
        setPending({ id: data.context.id, text });
        if (!getChatInputContextIdFromPathname(currentPath.current)) router.replace('/app');
      }
    };
    window.addEventListener('message', onMessage);
    // Parent may have sent hello before React mounted. Its response validates
    // our exact origin/window and instance nonce; credentials never cross.
    if (panel) send('ready', { path: currentPath.current, capabilities: ['x-reply-v1'] });
    else window.parent.postMessage({ channel: 'gab-browser-panel', type: 'listening' }, BROWSER_EXTENSION_ORIGIN);
    return () => { xReplies.cancel(); window.removeEventListener('message', onMessage); };
  }, [panel, router]);

  useEffect(() => {
    if (!panel || !macAvailable || !guest || attemptedSignIn.current) return;
    attemptedSignIn.current = true;
    let active = true;
    const send = (type, extra = {}) => window.parent.postMessage({ channel: 'gab-browser-panel', nonce: panel.nonce, type, ...extra }, BROWSER_EXTENSION_ORIGIN);
    const storageChanged = event => {
      if (event.key === 'auth-storage' || event.key === null) { active = false; send('mac-sign-in-complete'); }
    };
    window.addEventListener('storage', storageChanged);
    const request = async (action, body) => {
      const response = await fetchWithTimeout(`${API_HOST}/json/auth/browser/${action}`, {
        method: 'POST', credentials: 'omit', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
      }, 5000);
      if (!response.ok) throw new Error('Mac sign-in unavailable.');
      return response.json();
    };
    void signInBrowserPanel({ getState: useAuthStore.getState, request, send, current: () => active })
      .catch(() => { if (active) send('mac-sign-in-error'); });
    return () => { active = false; window.removeEventListener('storage', storageChanged); };
  }, [panel, macAvailable, guest]);

  useEffect(() => {
    if (!panel) return;
    if (priorUser.current && priorUser.current !== userId) {
      setPending(null); added.current.clear(); router.replace('/app');
    }
    priorUser.current = userId;
  }, [panel, userId, router]);

  useEffect(() => {
    if (!panel) return;
    window.parent.postMessage({ channel: 'gab-browser-panel', nonce: panel.nonce, type: 'ready', path: pathname, capabilities: ['x-reply-v1'] }, BROWSER_EXTENSION_ORIGIN);
  }, [panel, pathname, connected]);

  useEffect(() => {
    if (!panel || !pending) return;
    const contextId = getChatInputContextIdFromPathname(pathname);
    if (!contextId) return;
    if (!added.current.has(pending.id)) {
      const composer = useChatInputStore.getState();
      const prior = composer.inputStates[contextId]?.text || '';
      composer.setContext(contextId);
      composer.setText(`${prior}${prior ? '\n\n' : ''}${pending.text}`);
      added.current.add(pending.id);
      if (added.current.size > 100) added.current.delete(added.current.values().next().value);
    }
    window.parent.postMessage({ channel: 'gab-browser-panel', nonce: panel.nonce, type: 'context-added', id: pending.id }, BROWSER_EXTENSION_ORIGIN);
    setPending(null);
  }, [panel, pathname, pending]);
  return null;
}
